// Chat Interno (/chats) no padrão Aeros Dark (mockup "Tela Aeros Dark - Chat
// Interno e Modais"): coluna de conversas (360px) + conversa. A lógica de
// carregamento, socket, envio, edição/exclusão/encaminhamento é a de sempre;
// a apresentação vive em ChatList, ChatMessages e nos modais desta pasta.
import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import TimerIcon from "@material-ui/icons/Timer";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { toast } from "react-toastify";
import { has, isObject } from "lodash";

import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";
import NewChatModal from "./NewChatModal";
import ChatInfoModal from "./ChatInfoModal";
import ForwardModal from "./ForwardModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useResponsive from "../../utils/useResponsive";
import useOnlineUsers from "../../hooks/useOnlineUsers";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { chatTitle, isGroup, unreadOf, editMinutesLeft } from "./chatRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    // o <main> tem a AppBar de 60px acima; sem descontar, o composer some.
    root: { display: "flex", height: "calc(100% - 60px - var(--faixa-conta-h, 0px))", minHeight: 0, backgroundColor: theme.palette.aeros.background },
    listCol: { width: 360, flex: "none", borderRight: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`, display: "flex", flexDirection: "column", minHeight: 0, [theme.breakpoints.down("sm")]: { width: "100%", borderRight: "none" } },
    convCol: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 },
    editPaper: { width: 520, maxWidth: "calc(100vw - 32px)", margin: 16 },
    editTimer: { fontSize: 12, color: stateText(theme, AEROS.warning, "warning"), marginTop: 2, display: "flex", alignItems: "center", gap: 4, "& .MuiSvgIcon-root": { fontSize: 13 } },
    editHint: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 6 },
    quote: { marginTop: 12, borderLeft: `3px solid ${AEROS.warning}`, backgroundColor: theme.palette.aeros.surface, borderRadius: 8, padding: "8px 12px" },
    quoteName: { fontSize: 11, fontWeight: 700, color: stateText(theme, AEROS.warning, "warning") },
    quoteText: { fontSize: 12.5, color: theme.palette.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    strong: { color: theme.palette.text.primary, fontWeight: 600 },
  };
});

export default function Chat() {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { user, socket } = useContext(AuthContext);
  const isMdUp = useResponsive();
  const onlineUsers = useOnlineUsers();
  const isMounted = useRef(true);
  const scrollToBottomRef = useRef();

  const [currentChat, setCurrentChat] = useState({});
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [justOpenedChat, setJustOpenedChat] = useState(false);

  const [newModal, setNewModal] = useState(null); // "new" | "edit"
  // Header (ChatPopover) abre "Nova conversa" via /chats?new=1
  useEffect(() => {
    if (new URLSearchParams(location.search).get("new")) { setNewModal("new"); history.replace("/chats"); }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [infoOpen, setInfoOpen] = useState(false);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [editing, setEditing] = useState(null); // mensagem
  const [editText, setEditText] = useState("");
  const [deleting, setDeleting] = useState(null); // mensagem
  const [deleteChatOpen, setDeleteChatOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // { userId: name }
  const typingTimers = useRef({});

  const companyId = user?.companyId;

  useEffect(() => () => { isMounted.current = false; }, []);

  // ---- carga inicial ----
  const findChats = async () => {
    if (!isMounted.current) return { records: [], hasMore: false };
    setLoading(true);
    try {
      const { data } = await api.get("/chats");
      if (isMounted.current) setChats(data.records);
      return data;
    } catch (err) {
      return { records: [], hasMore: false };
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    findChats().then((data) => {
      const { records } = data;
      if (isMounted.current && records.length > 0 && id) {
        const chat = records.find((r) => r.uuid === id);
        if (chat) selectChat(chat);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // abrir por URL quando a lista já existe
  useEffect(() => {
    if (id && chats.length && (!currentChat.id || currentChat.uuid !== id)) {
      const chat = chats.find((r) => r.uuid === id);
      if (chat) selectChat(chat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, chats]);

  // ---- socket (mesma lógica de sempre) ----
  useEffect(() => {
    if (!user.id || !socket || !companyId) return undefined;
    const onChatUser = (data) => {
      if (!isMounted.current) return;
      if (data.action === "create") setChats((prev) => (prev.some((c) => c.id === data.record.id) ? prev : [data.record, ...prev]));
      if (data.action === "update") {
        // Chat.lastMessage no banco é string; a lista usa o objeto do ListService — preservar.
        const merge = (c) => ({ ...c, ...data.record, lastMessage: typeof data.record.lastMessage === "string" ? c.lastMessage : data.record.lastMessage, messagesCount: data.record.messagesCount ?? c.messagesCount });
        setChats((prev) => prev.map((c) => (c.id === data.record.id ? merge(c) : c)));
        if (currentChat && currentChat.id === data.record.id) setCurrentChat((prev) => merge(prev));
      }
      if (data.action === "mention" && data.chat && Number(data.message?.senderId) !== Number(user.id)) {
        toast.info(i18n.t("chatIndex.toasts.mentioned", { name: (data.message?.sender?.name || "").split(" ")[0], chat: chatTitle(data.chat, user.id) }), {
          onClick: () => history.push(`/chats/${data.chat.uuid}`),
        });
      }
    };
    const onChat = (data) => {
      if (!isMounted.current) return;
      if (data.action === "delete") {
        setChats((prev) => prev.filter((c) => c.id !== +data.id));
        if (currentChat?.id === +data.id) {
          setMessages([]);
          setMessagesPage(1);
          setMessagesPageInfo({ hasMore: false });
          setCurrentChat({});
          history.push("/chats");
        }
      }
      if (data.action === "new-message" || data.action === "update") {
        if (!currentChat || currentChat.id !== data.chat.id) {
          setChats((prev) => prev.map((c) => (c.id === data.chat.id ? { ...c, ...data.chat } : c)));
        }
      }
    };
    socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);
    socket.on(`company-${companyId}-chat`, onChat);
    return () => {
      socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
      socket.off(`company-${companyId}-chat`, onChat);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, socket, companyId, currentChat?.id]);

  useEffect(() => {
    if (!(isObject(currentChat) && has(currentChat, "id") && socket && companyId)) return undefined;
    const onCurrentChat = (data) => {
      if (!isMounted.current) return;
      if (data.action === "new-message") {
        setMessages((prev) => {
          const filtered = prev.filter((msg) => !(String(msg.id).startsWith("temp-") && msg.senderId === data.newMessage.senderId && msg.message === data.newMessage.message));
          if (filtered.some((msg) => msg.id === data.newMessage.id)) return filtered;
          return [...filtered, data.newMessage];
        });
        setChats((prev) => prev.map((c) => (c.id === data.newMessage.chatId ? { ...c, ...data.chat, lastMessage: data.newMessage, updatedAt: data.newMessage.createdAt } : c)));
        setTimeout(() => scrollToBottomRef.current && scrollToBottomRef.current(), 300);
      } else if (data.action === "edit-message" || data.action === "delete-message") {
        setMessages((prev) => prev.map((msg) => (msg.id === data.message.id ? { ...msg, ...data.message } : msg)));
      } else if (data.action === "typing") {
        if (Number(data.userId) === Number(user.id)) return;
        clearTimeout(typingTimers.current[data.userId]);
        if (data.typing) {
          setTypingUsers((prev) => ({ ...prev, [data.userId]: data.name || i18n.t("aeros.chat.someone") }));
          typingTimers.current[data.userId] = setTimeout(() => setTypingUsers((prev) => { const n = { ...prev }; delete n[data.userId]; return n; }), 3000);
        } else {
          setTypingUsers((prev) => { const n = { ...prev }; delete n[data.userId]; return n; });
        }
      }
      if (data.action === "update") {
        setChats((prev) => prev.map((c) => (c.id === data.chat.id ? { ...c, ...data.chat } : c)));
      }
    };
    socket.on(`company-${companyId}-chat-${currentChat.id}`, onCurrentChat);
    return () => {
      socket.off(`company-${companyId}-chat-${currentChat.id}`, onCurrentChat);
      setTypingUsers({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id, companyId, socket]);

  // ---- conversa ----
  const findMessages = async (chatId, isLoadMore = false, page = null) => {
    if (!isMounted.current || !chatId) return;
    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);
    const el = document.querySelector(".messageList");
    const prevHeight = el?.scrollHeight || 0;
    const prevTop = el?.scrollTop || 0;
    try {
      const pageToFetch = page !== null ? page : messagesPage;
      const { data } = await api.get(`/chats/${chatId}/messages?pageNumber=${pageToFetch}`);
      if (!isMounted.current) return;
      if (isLoadMore) setMessagesPage((p) => p + 1);
      setMessagesPageInfo(data);
      if (pageToFetch === 1) setMessages(data.records);
      else {
        setMessages((prev) => [...data.records, ...prev]);
        if (isLoadMore) {
          setTimeout(() => {
            const list = document.querySelector(".messageList");
            if (list) list.scrollTop = prevTop + (list.scrollHeight - prevHeight);
          }, 100);
        }
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) {
        if (isLoadMore) setLoadingMore(false);
        else setLoading(false);
      }
    }
  };

  const selectChat = async (chat) => {
    if (!isMounted.current) return;
    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo(null);
    setJustOpenedChat(true);
    try {
      const { data } = await api.get(`/chats/${chat.uuid}`);
      if (!isMounted.current) return;
      // GET /chats/:uuid volta sem participantes; mantém os da lista.
      setCurrentChat({ ...chat, ...data, users: data.users?.length ? data.users : chat.users, owner: data.owner || chat.owner });
      await findMessages(data.id, false, 1);
    } catch (err) {
      if (!isMounted.current) return;
      setCurrentChat(chat);
      await findMessages(chat.id, false, 1);
    }
  };

  const loadMoreMessages = async () => {
    if (!loadingMore && currentChat?.id && messagesPageInfo?.hasMore) await findMessages(currentChat.id, true);
  };

  const handleLoadNewChat = (data, existing = false) => {
    if (!isMounted.current) return;
    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo(null);
    setCurrentChat(data);
    setJustOpenedChat(true);
    history.push(`/chats/${data.uuid}`);
    selectChat(data);
    if (!existing) findChats();
  };

  const addOptimisticMessage = (msg) => setMessages((prev) => [...prev, msg]);

  // ---- ações de mensagem ----
  const submitEdit = async () => {
    if (!editing || !editText.trim()) return;
    setBusy(true);
    try {
      await api.put(`/chats/messages/${editing.id}`, { message: editText });
      setMessages((prev) => prev.map((msg) => (msg.id === editing.id ? { ...msg, message: editText, isEdited: true } : msg)));
      setEditing(null);
      setEditText("");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/chats/messages/${deleting.id}`);
      setMessages((prev) => prev.map((msg) => (msg.id === deleting.id ? { ...msg, isDeleted: true, mediaPath: null, forwardedFrom: null, replyTo: null } : msg)));
      setDeleting(null);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // ---- ações da conversa ----
  const deleteChat = async () => {
    setBusy(true);
    try {
      await api.delete(`/chats/${currentChat.id}`);
      setDeleteChatOpen(false);
      setChats((prev) => prev.filter((c) => c.id !== currentChat.id));
      setCurrentChat({});
      setMessages([]);
      history.push("/chats");
      toast.success(i18n.t("chatIndex.toasts.chatDeleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const leaveGroup = async () => {
    setBusy(true);
    try {
      await api.post(`/chats/${currentChat.id}/leave`);
      setLeaveOpen(false);
      setChats((prev) => prev.filter((c) => c.id !== currentChat.id));
      setCurrentChat({});
      setMessages([]);
      history.push("/chats");
      toast.success(i18n.t("chatIndex.toasts.left"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleMute = async () => {
    try {
      const { data } = await api.put(`/chats/${currentChat.id}/mute`);
      const patch = (c) => ({ ...c, users: (c.users || []).map((u) => (Number(u.userId) === Number(user.id) ? { ...u, muted: data.muted } : u)) });
      setChats((prev) => prev.map((c) => (c.id === currentChat.id ? patch(c) : c)));
      setCurrentChat((prev) => patch(prev));
      toast.success(i18n.t(data.muted ? "chatIndex.toasts.muted" : "chatIndex.toasts.unmuted"));
    } catch (err) {
      toastError(err);
    }
  };

  const markAllRead = async () => {
    const pendentes = chats.filter((c) => unreadOf(c, user.id) > 0);
    try {
      await Promise.all(pendentes.map((c) => api.post(`/chats/${c.id}/read`, { userId: user.id })));
      setChats((prev) => prev.map((c) => ({ ...c, users: (c.users || []).map((u) => (Number(u.userId) === Number(user.id) ? { ...u, unreads: 0 } : u)) })));
    } catch (err) {
      toastError(err);
    }
  };

  const hasChat = isObject(currentChat) && has(currentChat, "id");
  const showList = isMdUp || !hasChat;
  const showConv = isMdUp || hasChat;

  return (
    <div className={classes.root}>
      {showList && (
        <div className={classes.listCol}>
          <ChatList chats={chats} loading={loading && chats.length === 0} handleSelectChat={selectChat} onlineUsers={onlineUsers} onNew={() => setNewModal("new")} onMarkAllRead={markAllRead} />
        </div>
      )}
      {showConv && (
        <div className={classes.convCol}>
          <ChatMessages
            chat={hasChat ? currentChat : null}
            messages={messages}
            loading={loading}
            loadingMore={loadingMore}
            pageInfo={messagesPageInfo}
            handleLoadMore={loadMoreMessages}
            onEdit={(msg) => { setEditing(msg); setEditText(msg.message || ""); }}
            onDelete={(msg) => setDeleting(msg)}
            onForward={(msg) => setForwardMessage(msg)}
            justOpenedChat={justOpenedChat}
            setJustOpenedChat={setJustOpenedChat}
            scrollToBottomRef={scrollToBottomRef}
            addOptimisticMessage={addOptimisticMessage}
            onlineUsers={onlineUsers}
            onOpenInfo={() => setInfoOpen(true)}
            onEditGroup={() => setNewModal("edit")}
            onDeleteChat={() => setDeleteChatOpen(true)}
            onLeaveGroup={() => setLeaveOpen(true)}
            onToggleMute={toggleMute}
            typingUsers={typingUsers}
            onBack={isMdUp ? null : () => { setCurrentChat({}); history.push("/chats"); }}
          />
        </div>
      )}

      <NewChatModal open={Boolean(newModal)} mode={newModal || "new"} chat={newModal === "edit" ? currentChat : null} chats={chats} onlineUsers={onlineUsers} onClose={() => setNewModal(null)} onSaved={handleLoadNewChat} />
      <ChatInfoModal open={infoOpen} chat={hasChat ? currentChat : null} onlineUsers={onlineUsers} onClose={() => setInfoOpen(false)} onEditGroup={() => { setInfoOpen(false); setNewModal("edit"); }} />
      <ForwardModal open={Boolean(forwardMessage)} message={forwardMessage} chats={chats} onlineUsers={onlineUsers} onClose={() => setForwardMessage(null)} />

      <Dialog open={Boolean(editing)} onClose={busy ? undefined : () => setEditing(null)} maxWidth={false} classes={{ paper: classes.editPaper }}>
        <div className={m.form}>
          <div className={m.header}>
            <div>
              <div className={m.title}>{i18n.t("chatMessages.editMessage")}</div>
              <div className={classes.editTimer}><TimerIcon /> {i18n.t("chatMessages.editTimeLeft", { min: editing ? editMinutesLeft(editing.createdAt) : 0 })}</div>
            </div>
            <IconButton className={m.closeButton} onClick={() => setEditing(null)} disabled={busy} aria-label={i18n.t("aeros.header.close")}><CloseIcon /></IconButton>
          </div>
          <div className={m.body}>
            <TextField variant="outlined" className={m.input} multiline rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} autoFocus />
            <div className={classes.editHint} style={{ marginTop: -8 }}>{i18n.t("chatMessages.editHint")}</div>
          </div>
          <div className={m.footer}>
            <div className={m.footerActions}>
              <Button variant="outlined" className={m.cancelButton} onClick={() => setEditing(null)} disabled={busy}>{i18n.t("chatIndex.cancel")}</Button>
              <Button variant="contained" color="primary" className={m.submitButton} onClick={submitEdit} disabled={busy || !editText.trim()}>
                {i18n.t("chatIndex.save")}
                {busy && <CircularProgress size={22} className={m.buttonProgress} />}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      <AerosConfirmModal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={submitDelete}
        loading={busy}
        icon={<DeleteOutlineIcon />}
        title={i18n.t("chatMessages.deleteTitle")}
        description={
          deleting && (
            <>
              {i18n.t("chatMessages.deleteText")}
              <div className={classes.quote}>
                <div className={classes.quoteName}>{Number(deleting.senderId) === Number(user.id) ? i18n.t("aeros.chat.you") : deleting.sender?.name} · {new Date(deleting.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>
                <div className={classes.quoteText}>{deleting.message || deleting.mediaName || "…"}</div>
              </div>
            </>
          )
        }
        confirmLabel={i18n.t("chatMessages.deleteConfirm")}
      />
      <AerosConfirmModal
        open={deleteChatOpen}
        onClose={() => setDeleteChatOpen(false)}
        onConfirm={deleteChat}
        loading={busy}
        icon={<DeleteOutlineIcon />}
        title={i18n.t("chatIndex.deleteChat.title")}
        description={
          hasChat && (
            <>
              {isGroup(currentChat) ? i18n.t("chatIndex.deleteChat.groupBefore") : i18n.t("chatIndex.deleteChat.dmBefore")} <b className={classes.strong}>{chatTitle(currentChat, user.id)}</b> {i18n.t("chatIndex.deleteChat.middle")} <b className={classes.strong}>{i18n.t("chatIndex.deleteChat.messages", { count: Number(currentChat.messagesCount ?? chats.find((c) => c.id === currentChat.id)?.messagesCount ?? 0) })}</b> {i18n.t("chatIndex.deleteChat.after", { count: (currentChat.users || []).length })}
            </>
          )
        }
        confirmLabel={i18n.t("chatIndex.deleteChat.confirm")}
      />
      <AerosConfirmModal
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        onConfirm={leaveGroup}
        loading={busy}
        tone="warning"
        icon={<ExitToAppIcon />}
        title={i18n.t("chatIndex.leave.title", { name: hasChat ? chatTitle(currentChat, user.id) : "" })}
        description={i18n.t("chatIndex.leave.text")}
        confirmLabel={i18n.t("chatIndex.leave.confirm")}
      />
    </div>
  );
}
